import React, { useState } from 'react'
import { Alert, Button, Card, Flex, Form, Input, Modal, Typography } from 'antd'
import { CodeOutlined, AppstoreAddOutlined } from '@ant-design/icons'
import { useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { formatPrice } from '../../subscription/pricing'
import { useCreateSite, useSiteLimit } from '../hooks'
import { canAddWebsite, ExtraWebsiteNotice } from './extra-website-notice'

type Choice = 'wordpress' | 'snippet' | null

/**
 * The address as typed, with https:// in front when the scheme is missing or mistyped ("https//", "https:/", "https:").
 */
function websiteAddress(typed: string): string {
    const address = typed.trim()
    const scheme = address.match(/^(https?)(:\/*|\/+)/i)

    if (scheme) {
        return `${scheme[1].toLowerCase()}://${address.slice(scheme[0].length)}`
    }

    return `https://${address}`
}

/**
 * Adds a website: with the WordPress plugin, or with a code snippet on any other website.
 */
export function AddSiteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const create = useCreateSite()
    const [choice, setChoice] = useState<Choice>(null)
    const [confirmedExtra, setConfirmedExtra] = useState(false)
    const [form] = Form.useForm<{ url: string; name?: string }>()
    const { data: limit } = useSiteLimit(open)
    const extraWebsite = limit && limit.connected >= limit.limit && limit.extraAvailable ? limit.extraWebsite : null

    const close = () => {
        setChoice(null)
        setConfirmedExtra(false)
        form.resetFields()
        create.reset()
        onClose()
    }

    const submit = ({ url, name }: { url: string; name?: string }) => {
        const address = websiteAddress(url)

        create.mutate(
            { url: address, name: name?.trim() || undefined, confirmExtraWebsite: confirmedExtra },
            {
                onSuccess: (site) => {
                    close()
                    navigate({ to: '/websites/$siteId/settings/connection', params: { siteId: site.id } })
                },
                onError: (error) => {
                    const message = isAxiosError<{ errors?: Record<string, string[]> }>(error)
                        ? error.response?.data?.errors?.url?.[0]
                        : undefined

                    if (message) {
                        form.setFields([{ name: 'url', errors: [message] }])
                    }
                },
            }
        )
    }

    const option = (key: Exclude<Choice, null>, icon: React.ReactNode, title: string, text: string) => (
        <Card
            hoverable
            onClick={() => setChoice(key)}
            className="flex-1"
        >
            <Flex
                vertical
                gap="small"
            >
                <span className="text-2xl">{icon}</span>
                <Typography.Text strong>{title}</Typography.Text>
                <Typography.Text type="secondary">{text}</Typography.Text>
            </Flex>
        </Card>
    )

    return (
        <Modal
            open={open}
            title={t('Add website')}
            onCancel={close}
            footer={null}
            width={640}
            destroyOnHidden
        >
            {choice === null && (
                <Flex
                    gap="middle"
                    wrap
                >
                    {option(
                        'snippet',
                        <CodeOutlined />,
                        t('Any website'),
                        t(
                            'Paste one line of code on your website. Works with Webflow, Wix, Squarespace, Shopify, Google Tag Manager and your own website.'
                        )
                    )}
                    {option(
                        'wordpress',
                        <AppstoreAddOutlined />,
                        t('WordPress'),
                        t(
                            'Using WordPress? With the plugin, Evaa always knows what is on your website right now, and you also see the chats.'
                        )
                    )}
                </Flex>
            )}

            {choice === 'wordpress' && (
                <Flex
                    vertical
                    gap="middle"
                >
                    <ol className="mb-0 list-decimal pl-5">
                        <li>{t('Install and activate the Evaa plugin in WordPress.')}</li>
                        <li>{t('Open Evaa in the WordPress menu and click "Connect to Evaa".')}</li>
                    </ol>
                    {/* The price is agreed to when connecting; said here too, so it is no surprise in WordPress. */}
                    {extraWebsite && (
                        <Alert
                            type="info"
                            showIcon
                            message={t(
                                'All websites of your plan are in use. Connecting adds an extra website for {{price}}, excl. VAT. You confirm this when connecting.',
                                {
                                    price:
                                        extraWebsite.period === 'yearly'
                                            ? t('{{price}} per year', {
                                                  price: formatPrice(extraWebsite.amount, extraWebsite.currency),
                                              })
                                            : t('{{price}} per month', {
                                                  price: formatPrice(extraWebsite.amount, extraWebsite.currency),
                                              }),
                                }
                            )}
                        />
                    )}
                    <Flex
                        justify="space-between"
                        gap="small"
                    >
                        <Button onClick={() => setChoice(null)}>{t('Back')}</Button>
                        <Button
                            type="primary"
                            onClick={close}
                        >
                            {t('Done')}
                        </Button>
                    </Flex>
                </Flex>
            )}

            {choice === 'snippet' && (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={submit}
                    requiredMark={false}
                >
                    <Form.Item
                        name="url"
                        label={t('Link to your website')}
                        rules={[{ required: true, message: t('Enter the link to your website') }]}
                        extra={t('Evaa reads the public pages of this website.')}
                    >
                        <Input
                            autoFocus
                            placeholder="https://www.jouwbedrijf.nl"
                        />
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label={t('Name (optional)')}
                    >
                        <Input placeholder={t('For example: Zorggroep Vlinder')} />
                    </Form.Item>
                    <div className="mb-4">
                        <ExtraWebsiteNotice
                            limit={limit}
                            confirmed={confirmedExtra}
                            onConfirmedChange={setConfirmedExtra}
                        />
                    </div>
                    <Flex
                        justify="space-between"
                        gap="small"
                    >
                        <Button onClick={() => setChoice(null)}>{t('Back')}</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={create.isPending}
                            disabled={!canAddWebsite(limit, confirmedExtra)}
                        >
                            {t('Add website')}
                        </Button>
                    </Flex>
                </Form>
            )}
        </Modal>
    )
}
